class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = null;
    this.summaryToggle = null;
    this.onBodyClickEvent = null;
    this._boundHandlers = {};
  }

  connectedCallback() {
    this.detailsContainer = this.querySelector('details') || this;
    this.summaryToggle = this.querySelector('summary');

    console.debug('details-modal: connectedCallback', {
      el: this,
      detailsContainer: !!this.detailsContainer,
      summaryToggle: !!this.summaryToggle,
      open: this.detailsContainer && this.detailsContainer.hasAttribute && this.detailsContainer.hasAttribute('open'),
    });

    // bind and store handlers so we can remove later
    this._boundHandlers.escapeKey = (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close();
    if (this.detailsContainer) {
      this.detailsContainer.addEventListener('keyup', this._boundHandlers.escapeKey);
    }

    if (this.summaryToggle) {
      this._boundHandlers.onSummaryClick = this.onSummaryClick.bind(this);
      this.summaryToggle.addEventListener('click', this._boundHandlers.onSummaryClick);
      this.summaryToggle.setAttribute('role', 'button');
    }

    // Bind close to the explicit modal close button(s)
    const closeBtn = this.querySelector('.modal__close-button, .search-modal__close-button, button.modal__close-button, [data-modal-close]') || this.querySelector('button[type="button"]');
    if (closeBtn) {
      this._boundHandlers.onCloseClick = (e) => { e.preventDefault(); this.close(true); };
      closeBtn.addEventListener('click', this._boundHandlers.onCloseClick);
    }

    // Delegated click handler: cover dynamic buttons or replaced nodes
    this._boundHandlers.onDelegatedClick = (e) => {
      try {
        const closeSelector = '.modal__close-button, .search-modal__close-button, button.modal__close-button, [data-modal-close]';
        const closeTarget = e.target.closest(closeSelector);
        if (closeTarget) {
          console.debug('details-modal: delegated click caught closeTarget', closeTarget);
          e.preventDefault();
          this.close(true);
          return;
        }

        // Overlay clicks: close the modal if clicked on overlay element
        if (e.target.classList && e.target.classList.contains('modal-overlay')) {
          e.preventDefault();
          this.close(false);
          return;
        }
      } catch (err) {
        /* ignore */
      }
    };

    // Listen in both bubble and capture phases to ensure we catch clicks
    this.addEventListener('click', this._boundHandlers.onDelegatedClick);
    this.addEventListener('click', this._boundHandlers.onDelegatedClick, true);

    // MutationObserver to detect dynamically inserted/updated close buttons
    try {
      this._boundHandlers.mutationObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of Array.from(m.addedNodes || [])) {
            if (!(node instanceof Element)) continue;
            const close = node.matches && node.matches('.search-modal__close-button, .modal__close-button, button.modal__close-button, [data-modal-close]') ? node : node.querySelector && node.querySelector('.search-modal__close-button, .modal__close-button, button.modal__close-button, [data-modal-close]');
            if (close) {
              try {
                console.debug('details-modal: mutation observer found close button', close);
                if (this._boundHandlers.dynamicClose) close.removeEventListener('click', this._boundHandlers.dynamicClose);
                this._boundHandlers.dynamicClose = (e) => { e.preventDefault(); this.close(true); };
                close.addEventListener('click', this._boundHandlers.dynamicClose);
              } catch (e) { /* ignore */ }
            }
          }
        }
      });
      this._boundHandlers.mutationObserver.observe(this, { childList: true, subtree: true });
    } catch (err) {
      /* ignore */
    }
  }

  disconnectedCallback() {
    try {
      if (this.detailsContainer && this._boundHandlers.escapeKey) {
        this.detailsContainer.removeEventListener('keyup', this._boundHandlers.escapeKey);
      }
      if (this.summaryToggle && this._boundHandlers.onSummaryClick) {
        this.summaryToggle.removeEventListener('click', this._boundHandlers.onSummaryClick);
      }
      const closeBtn = this.querySelector('.modal__close-button, .search-modal__close-button, button.modal__close-button, [data-modal-close]') || this.querySelector('button[type="button"]');
      if (closeBtn && this._boundHandlers.onCloseClick) {
        closeBtn.removeEventListener('click', this._boundHandlers.onCloseClick);
      }
    } catch (e) {
      /* ignore cleanup errors */
    }
    if (this._boundHandlers.onDelegatedClick) {
      this.removeEventListener('click', this._boundHandlers.onDelegatedClick);
      this.removeEventListener('click', this._boundHandlers.onDelegatedClick, true);
    }
    try {
      if (this._boundHandlers.mutationObserver) {
        this._boundHandlers.mutationObserver.disconnect();
        delete this._boundHandlers.mutationObserver;
      }
    } catch (e) { /* ignore */ }
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    console.debug('details-modal: open()', { eventTarget: event && event.target, detailsContainer: !!this.detailsContainer });

    // Add a document-level capture listener to ensure close clicks are caught.
    // Also add a coordinate-based pointerup fallback so clicks are handled
    // even if an overlay or spinner intercepts the event target.
    try {
      const closeSelector = '.modal__close-button, .search-modal__close-button, button.modal__close-button, [data-modal-close]';
      this._boundHandlers.documentClose = (e) => {
        try {
          const closeTarget = e.target.closest && e.target.closest(closeSelector);
          console.debug('details-modal: documentClose', { x: e.clientX, y: e.clientY, target: e.target, closeTarget });
          if (closeTarget) {
            e.preventDefault();
            this.close(true);
            return;
          }
        } catch (err) {
          /* ignore */
        }
      };
      document.addEventListener('click', this._boundHandlers.documentClose, true);

      // Also listen for pointerdown/touchstart early to bypass propagation-stopping handlers
      this._boundHandlers.documentPointerDown = (e) => {
        try {
          const closeTarget = e.target.closest && e.target.closest(closeSelector);
          console.debug('details-modal: documentPointerDown', { x: e.clientX, y: e.clientY, target: e.target, closeTarget });
          if (closeTarget) {
            e.preventDefault();
            this.close(true);
            return;
          }
          if (e.target.classList && e.target.classList.contains('modal-overlay')) {
            e.preventDefault();
            this.close(false);
            return;
          }
        } catch (err) {
          /* ignore */
        }
      };
      document.addEventListener('pointerdown', this._boundHandlers.documentPointerDown, true);

      // Pointerup coordinate-based fallback: if the pointerup occurs within the
      // bounding rect of any registered close button, force close the modal.
      this._boundHandlers.documentPointerUp = (e) => {
        try {
          if (!this.isOpen()) return;
          const x = e.clientX;
          const y = e.clientY;
          console.debug('details-modal: documentPointerUp', { x, y, elementAt: document.elementFromPoint(x, y) });
          const rectContains = (el) => {
            if (!el) return false;
            const r = el.getBoundingClientRect();
            return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
          };

          // Check known close button inside this modal first
          const localClose = this.querySelector(closeSelector);
          if (localClose && rectContains(localClose)) {
            console.debug('details-modal: pointerUp matched localClose', localClose);
            e.preventDefault();
            this.close(true);
            return;
          }

          // Also check any global close candidates (covers dynamically moved nodes)
          const candidates = document.querySelectorAll(closeSelector);
          for (const c of Array.from(candidates)) {
            if (rectContains(c)) {
              console.debug('details-modal: pointerUp matched candidate', c);
              e.preventDefault();
              this.close(true);
              return;
            }
          }
        } catch (err) {
          /* ignore */
        }
      };
      document.addEventListener('pointerup', this._boundHandlers.documentPointerUp, true);
    } catch (err) {
      /* ignore */
    }

    // Re-bind close button in case it's dynamically replaced when modal opens
    try {
      const closeBtn = this.querySelector('.search-modal__close-button, .modal__close-button, button.modal__close-button, [data-modal-close]');
      if (closeBtn) {
        if (this._boundHandlers.dynamicClose) closeBtn.removeEventListener('click', this._boundHandlers.dynamicClose);
        this._boundHandlers.dynamicClose = (e) => { e.preventDefault(); this.close(true); };
        closeBtn.addEventListener('click', this._boundHandlers.dynamicClose);
        // Ensure the close button sits above overlays/spinners and accepts pointer events
        try { closeBtn.style.zIndex = closeBtn.style.zIndex || '1001'; } catch (e) {}
        try { closeBtn.style.pointerEvents = 'auto'; } catch (e) {}
      }
    } catch (err) {
      /* ignore */
    }

    // Make any spinner elements non-interactive and pause animations so they don't block or animate
    try {
      const spinners = this.querySelectorAll('.spinner');
      this._boundHandlers.__spinners = Array.from(spinners);
      for (const s of this._boundHandlers.__spinners) {
        try {
          s.dataset._origPointerEvents = s.style.pointerEvents || '';
          s.dataset._origAnimationPlayState = s.style.animationPlayState || '';
          s.dataset._origTransition = s.style.transition || '';
          s.style.pointerEvents = 'none';
          s.style.animationPlayState = 'paused';
          s.style.transition = 'none';
        } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }

    // Debug: add a visible close button so we can debug click/visibility issues
    try {
      const debugBtn = document.createElement('button');
      debugBtn.className = 'details-modal-debug-close';
      debugBtn.setAttribute('aria-label', 'Close debug');
      debugBtn.innerText = '×';
      Object.assign(debugBtn.style, {
        position: 'fixed',
        top: '12px',
        right: '12px',
        zIndex: '10002',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        border: 'none',
        padding: '6px 10px',
        borderRadius: '4px',
        fontSize: '20px',
        cursor: 'pointer',
        pointerEvents: 'auto'
      });
      this._boundHandlers.debugButtonRef = debugBtn;
      this._boundHandlers.debugButtonClick = (e) => { e.preventDefault(); this.close(true); };
      debugBtn.addEventListener('click', this._boundHandlers.debugButtonClick);
      document.body.appendChild(debugBtn);
    } catch (e) { /* ignore */ }

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    console.debug('details-modal: close()', { focusToggle, isOpen: this.isOpen() });
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
    // remove document-level capture listener if present
    try {
      if (this._boundHandlers.documentClose) {
        document.removeEventListener('click', this._boundHandlers.documentClose, true);
        delete this._boundHandlers.documentClose;
      }
      if (this._boundHandlers.documentPointerDown) {
        document.removeEventListener('pointerdown', this._boundHandlers.documentPointerDown, true);
        delete this._boundHandlers.documentPointerDown;
      }
      if (this._boundHandlers.documentPointerUp) {
        document.removeEventListener('pointerup', this._boundHandlers.documentPointerUp, true);
        delete this._boundHandlers.documentPointerUp;
      }
      if (this._boundHandlers.dynamicClose) {
        const closeBtn = this.querySelector('.search-modal__close-button, .modal__close-button, button.modal__close-button, [data-modal-close]');
        try { if (closeBtn) closeBtn.removeEventListener('click', this._boundHandlers.dynamicClose); } catch(e) {}
        delete this._boundHandlers.dynamicClose;
      }
      // Restore any spinner styles we paused for debugging
      if (this._boundHandlers.__spinners) {
        for (const s of this._boundHandlers.__spinners) {
          try {
            s.style.pointerEvents = s.dataset._origPointerEvents || '';
            s.style.animationPlayState = s.dataset._origAnimationPlayState || '';
            s.style.transition = s.dataset._origTransition || '';
            delete s.dataset._origPointerEvents;
            delete s.dataset._origAnimationPlayState;
            delete s.dataset._origTransition;
          } catch(e) { /* ignore */ }
        }
        delete this._boundHandlers.__spinners;
      }

      // Remove debug close button if present
      if (this._boundHandlers.debugButtonRef) {
        try {
          this._boundHandlers.debugButtonRef.removeEventListener('click', this._boundHandlers.debugButtonClick);
          if (this._boundHandlers.debugButtonRef.parentNode) this._boundHandlers.debugButtonRef.parentNode.removeChild(this._boundHandlers.debugButtonRef);
        } catch(e) { /* ignore */ }
        delete this._boundHandlers.debugButtonClick;
        delete this._boundHandlers.debugButtonRef;
      }
    } catch (err) {
      /* ignore */
    }
  }
}

customElements.define('details-modal', DetailsModal);
